/**
 * Margin design tokens — JS side.
 *
 * Tailwind owns colors, spacing, radii, shadows, and the type scale.
 * This file owns motion (Framer Motion springs/transitions), the accent
 * palette options the user can pick from, and a few constants that need
 * to live in JS (e.g. animation durations consumed by setTimeout).
 */

import type { Transition } from 'framer-motion';

// ---------- Motion ----------

/** Default UI spring. Used for card entries, layout transitions, list reorder. */
export const springDefault: Transition = {
  type: 'spring',
  stiffness: 250,
  damping: 25,
};

/** Gentler spring for modals, bottom sheets, full-screen transitions. */
export const springGentle: Transition = {
  type: 'spring',
  stiffness: 200,
  damping: 28,
};

/** Snappy spring for tap feedback and small interactive flourishes. */
export const springQuick: Transition = {
  type: 'spring',
  stiffness: 400,
  damping: 30,
};

/** Tap feedback applied to every interactive element. */
export const tapScale = { scale: 0.97 };

/** Standard card entry: fade + 12px lift, springy. */
export const cardEntry: Transition = springDefault;

/** Stagger between sibling card entries, in seconds. */
export const staggerSiblings = 0.05;

// ---------- Accent palette ----------

/**
 * Accent color options the user picks from in Settings.
 * Each entry exposes both the light and dark RGB triplet (space-separated)
 * so the theme manager can write the correct value into --accent
 * depending on resolved theme.
 */
export const accentPalette = {
  blue: { light: '0 122 255', dark: '10 132 255' },
  indigo: { light: '88 86 214', dark: '94 92 230' },
  teal: { light: '90 200 250', dark: '100 210 255' },
  mint: { light: '0 199 190', dark: '102 212 207' },
  green: { light: '52 199 89', dark: '48 209 88' },
  yellow: { light: '255 204 0', dark: '255 214 10' },
  orange: { light: '255 149 0', dark: '255 159 10' },
  red: { light: '255 59 48', dark: '255 69 58' },
  pink: { light: '255 45 85', dark: '255 55 95' },
  purple: { light: '175 82 222', dark: '191 90 242' },
  brown: { light: '162 132 94', dark: '172 142 104' },
} as const;

export type AccentKey = keyof typeof accentPalette;

/** Stellar theme accent — unlocked after rating the app. */
export const stellarAccent = { light: '167 139 250', dark: '191 167 254' } as const;

// ---------- Layout ----------

/** Max content width — matches Tailwind's `max-w-content`. */
export const contentMaxWidth = 428;

/** Apple HIG minimum tap target. */
export const tapMinSize = 44;
